import { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '@/App';
import {
  queueAction,
  processSyncQueue,
  getPendingCount,
  clearExpiredCache,
} from '@/lib/offlineDb';

// Poll interval for pending count (5 seconds)
const POLL_INTERVAL_MS = 5000;
// Clean expired cache every 15 minutes
const CACHE_CLEANUP_INTERVAL_MS = 15 * 60 * 1000;

/**
 * useOfflineSync - manages online/offline detection, sync queue, and auto-sync.
 *
 * Returns:
 *  - isOnline: boolean - current connectivity state
 *  - pendingSyncCount: number - items waiting to sync
 *  - syncing: boolean - whether sync is in progress
 *  - lastSyncTime: Date|null - last successful sync
 *  - syncNow: () => Promise - manually trigger sync
 *  - queue: (action) => Promise - queue a write operation
 */
export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const syncingRef = useRef(false);

  // Sync function - processes all pending queue items
  const syncNow = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      await processSyncQueue(axiosInstance);
      const count = await getPendingCount();
      setPendingSyncCount(count);
      setLastSyncTime(new Date());
    } catch (err) {
      console.error('[OfflineSync] Sync failed:', err);
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      syncNow();
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncNow]);

  // Poll pending count every 5 seconds
  useEffect(() => {
    // Initial check
    getPendingCount().then(setPendingSyncCount).catch(() => {});

    const interval = setInterval(async () => {
      try {
        const count = await getPendingCount();
        setPendingSyncCount(count);
      } catch {
        // IndexedDB may not be available in some environments
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // Periodically clean expired cache
  useEffect(() => {
    const interval = setInterval(() => {
      clearExpiredCache().catch(() => {});
    }, CACHE_CLEANUP_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // Queue an action (called by other hooks when offline)
  const queue = useCallback(async (action) => {
    await queueAction(action);
    const count = await getPendingCount();
    setPendingSyncCount(count);
  }, []);

  return {
    isOnline,
    pendingSyncCount,
    syncing,
    lastSyncTime,
    syncNow,
    queue,
  };
}
